<template>
    <form
        :style="{ display: isOpen ? 'block' : 'none' }"
        class="form form--tax-office-list-field tax-office-list-field field--tax-office-list"
        @submit.prevent="null"
    >
        <button
            type="button"
            class="tax-office-list-field__close"
            aria-label="Close tax offices list"
            @click="close"
        />
        <div class="row row--tax-offices">
            <h4 class="row--tax-offices__header">
                {{ $t(`${TRANSLATION_KEY}.taxOffices`) }}
            </h4>
            <Field
                v-model="formData.value"
                type="search"
                name="tax-office"
                :placeholder="$t(`${TRANSLATION_KEY}.searchPlaceholder`)"
                cy-data="search-field"
                :no-auto-validation="true"
            />
            <ul class="tax-offices-list" cy-data="office-list">
                <li
                    v-for="value in filteredTaxOffices"
                    :key="value[0]"
                    :data-id="value[0]"
                    tabindex="0"
                    role="button"
                    class="tax-office-list-field__option"
                    @click="setValue(value[0], value[1])"
                    @keydown.enter.prevent="setValue(value[0], value[1])"
                    @keydown.space.prevent="setValue(value[0], value[1])"
                >
                    {{ value[1] }}
                </li>
            </ul>
        </div>
    </form>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted } from 'vue';
import Field from '@/components/basic/fields/Field.vue';
import { useStore } from 'vuex';

export default defineComponent({
    name: 'TaxOfficeListField',
    components: {
        Field,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        name: {
            type: String,
            required: true,
        },
        taxOfficeDataForeigner: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        passedData: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        taxResidence: {
            type: String,
            required: false,
            default: '',
        },
    },
    emits: ['close', 'office-selected'],
    setup(props, { emit }) {
        const store = useStore();
        const TRANSLATION_KEY = 'compositeFields.TaxOfficeListField';

        const taxOffices = ref(store.state.config.taxOffices);
        const filteredTaxOffices = ref(store.state.config.taxOffices);

        const formData = ref({
            key: '',
            value: '',
        });

        const returnService = () => {
            if (props.passedData) {
                for (const entry in props.passedData) {
                    if (
                        Object.prototype.hasOwnProperty.call(
                            formData.value,
                            entry
                        )
                    ) {
                        (formData.value as any)[entry] = (
                            props.passedData as any
                        )[entry];
                    }
                }
            }
        };

        const close = () => {
            emit('close');
        };

        const setDefaultTaxOfficeForForeigner = () => {
            if (
                props.taxOfficeDataForeigner &&
                Object.keys(props.taxOfficeDataForeigner).length
            ) {
                const defaultForeignerTaxOffice = [
                    String(props.taxOfficeDataForeigner.key),
                    props.taxOfficeDataForeigner.value,
                ];

                taxOffices.value = [
                    defaultForeignerTaxOffice,
                    ...taxOffices.value.filter(
                        (item: any) => item[0] !== defaultForeignerTaxOffice[0]
                    ),
                ];

                setValue(
                    props.taxOfficeDataForeigner.key,
                    props.taxOfficeDataForeigner.value
                );
            }
        };

        const setValue = (id: any, text: any) => {
            formData.value.key = id;
            formData.value.value = text;
            emit('office-selected', formData.value);
        };

        const search = () => {
            if (!taxOffices.value.length) {
                taxOffices.value = store.state.config.taxOffices;
            }

            filteredTaxOffices.value = taxOffices.value.filter((entry: any) =>
                entry[1]
                    .toLowerCase()
                    .includes(formData.value.value.trim().toLowerCase())
            );
        };

        const clearFormData = () => {
            formData.value.key = '';
            formData.value.value = '';
        };

        watch(
            () => formData.value.value,
            () => {
                search();
            }
        );

        watch(
            () => props.taxOfficeDataForeigner,
            () => {
                setDefaultTaxOfficeForForeigner();
            }
        );

        onMounted(() => {
            returnService();
            setDefaultTaxOfficeForForeigner();
        });

        return {
            TRANSLATION_KEY,
            taxOffices,
            filteredTaxOffices,
            formData,
            close,
            setValue,
            search,
            clearFormData,
        };
    },
});
</script>

<style scoped>
.tax-office-list-field {
    display: block;
}
</style>
